import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Paper,
  alpha,
  styled,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { shopsService } from '../../services/api';
import { useShop } from '../../contexts/ShopContext';
import { Shop } from '../../types';
import { getShopUrl, getShopLoginUrl, getShopBookingUrl, getShopSlug } from '../../utils/shopUtils';
import { motion } from 'motion/react';

// Array of local placeholder image paths (assuming they exist in public/images/shops/)
const localShopImages = Array.from({ length: 10 }, (_, i) => 
  `/images/shops/shop-placeholder-${i + 1}.jpg` // Construct paths like /images/shops/shop-placeholder-1.jpg
);

// Styled components for the themed UI
const GradientBackground = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #1a1b1f 0%, #2d2e35 100%)',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  minHeight: '100vh',
}));

const GradientOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at 50% 30%, rgba(82, 109, 254, 0.15) 0%, transparent 70%)',
  zIndex: 0,
}));

const BackgroundLines = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
  backgroundPosition: 'center center',
  zIndex: 0,
  opacity: 0.5,
}));

// Styled paper with glassmorphism effect
const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(8px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

// Styled card with glassmorphism effect
const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(8px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
    background: 'rgba(255, 255, 255, 0.08)',
  }
}));

// Gradient button
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
  color: '#fff',
  fontWeight: 600,
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(58, 134, 255, 0.3), 0 0 15px rgba(255, 215, 0, 0.6)',
  border: '2px solid rgba(255, 223, 0, 0.7)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1, 
    bottom: -1,
    borderRadius: '10px',
    background: 'linear-gradient(45deg, #FFD700, #FFA500)',
    opacity: 0.4,
    filter: 'blur(3px)',
    zIndex: -1
  },
  '&:hover': {
    boxShadow: '0 8px 25px rgba(58, 134, 255, 0.5), 0 0 25px rgba(255, 215, 0, 0.7)',
    background: 'linear-gradient(90deg, #4361ee 0%, #3a86ff 100%)',
    border: '2px solid rgba(255, 223, 0, 0.9)',
    transform: 'translateY(-2px)',
    transition: 'all 0.3s ease'
  }
}));

// Section heading
const SectionHeading = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -8,
    height: 4,
    width: 60,
    background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
    boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
    borderRadius: 4,
  }
}));

// Styled search field
const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.07)',
    borderRadius: '12px',
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3a86ff',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputAdornment-root': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
}));

const ShopsListPage: React.FC = () => {
  console.log('ShopsListPage component is rendering');

  const [shops, setShops] = useState<Shop[]>([]);
  const [filteredShops, setFilteredShops] = useState<Shop[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const baseUrl = window.location.origin;
  const { setSelectedShopBySlug } = useShop();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchShops = async () => {
      try {
        setLoading(true);
        const shopsData = await shopsService.getAllShops();
        const activeShops = shopsData.filter(shop => shop.isActive);
        setShops(activeShops);
        setFilteredShops(activeShops);
      } catch (err) {
        console.error('Failed to load shops', err);
        setError('Failed to load shops. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchShops();
  }, []);
  
  useEffect(() => {
    if (searchTerm) {
      const filtered = shops.filter(shop => 
        shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredShops(filtered);
    } else {
      setFilteredShops(shops);
    }
  }, [searchTerm, shops]);
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopiedLink(url);
        setTimeout(() => setCopiedLink(null), 3000);
      })
      .catch(err => {
        console.error('Failed to copy', err);
      });
  };
  
  const getFullUrl = (relativeUrl: string) => `${baseUrl}/#${relativeUrl}`;
  
  const handleSelectShop = async (shop: Shop) => {
    const slug = getShopSlug(shop);
    try {
      await setSelectedShopBySlug(slug);
      navigate('/');
    } catch (error) {
      console.error('Failed to select shop', error);
      setError('Failed to select shop. Please try again.');
    }
  };
  
  const renderShopCards = () => {
    if (loading) {
      return Array(6).fill(0).map((_, i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`skeleton-${i}`}>
          <GlassCard>
            <Skeleton variant="rectangular" height={140} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            <CardContent sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" height={40} width="80%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              <Skeleton variant="text" height={20} width="60%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              <Skeleton variant="text" height={20} width="40%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            </CardContent>
            <CardActions>
              <Skeleton variant="rectangular" height={36} width={80} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            </CardActions>
          </GlassCard>
        </Grid>
      ));
    }
    
    if (filteredShops.length === 0 && !loading) {
      return (
        <Grid size={12}>
          <Alert severity="info" sx={{ 
            background: 'rgba(58, 134, 255, 0.1)', 
            color: '#fff', 
            border: '1px solid rgba(58, 134, 255, 0.2)'
          }}>
            No shops found{searchTerm ? ' matching your search' : ''}.
          </Alert>
        </Grid>
      );
    }
    
    return filteredShops.map((shop, index) => {
      const shopUrl = getShopUrl(shop);
      const loginUrl = getShopLoginUrl(shop);
      const bookingUrl = getShopBookingUrl(shop);
      
      // Select a local image path based on index
      const imageUrl = localShopImages[index % localShopImages.length];
      
      return (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={shop.id}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 * index }}
          >
            <GlassCard>
              <CardMedia
                component="img"
                sx={{
                  height: 180, 
                  objectFit: 'cover',
                  bgcolor: 'grey.800',
                }}
                image={imageUrl}
                alt={`Image of a mechanic workshop for ${shop.name}`}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="h2" sx={{ color: '#fff' }}>
                  {shop.name}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                  {shop.address}
                </Typography>
                {shop.phoneNumber && (
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                    {shop.phoneNumber}
                  </Typography>
                )}
                {shop.email && (
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                    {shop.email}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                <GradientButton 
                  size="small" 
                  onClick={() => handleSelectShop(shop)}
                  startIcon={<StorefrontIcon />}
                  sx={{ fontWeight: 'bold', px: 3 }}
                >
                  Select Shop
                </GradientButton>
                <Box>
                  <Tooltip title="Go to Shop Login">
                    <IconButton 
                      component={RouterLink} 
                      to={loginUrl} 
                      size="small"
                      sx={{ 
                        color: alpha('#fff', 0.7),
                        '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.1)' } 
                      }}
                    >
                      <LockOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Go to Shop Booking">
                    <IconButton 
                      component={RouterLink} 
                      to={bookingUrl} 
                      size="small"
                      sx={{ 
                        color: alpha('#fff', 0.7),
                        '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.1)' }  
                      }}
                    >
                      <EventAvailableIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Shop URL">
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopy(getFullUrl(shopUrl))}
                      sx={{ 
                        color: alpha('#fff', 0.7),
                        '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.1)' }  
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </GlassCard>
          </motion.div>
        </Grid>
      );
    });
  };
  
  return (
    <GradientBackground>
      <GradientOverlay />
      <BackgroundLines />
      
      <Container sx={{ py: { xs: 4, md: 8 }, position: 'relative', zIndex: 1 }} maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <GlassPaper sx={{ p: { xs: 3, md: 4 }, mb: 6 }}>
            <SectionHeading variant="h4" align="center" sx={{ mb: 3 }}>
              Select Your Shop
            </SectionHeading>
            <Typography variant="subtitle1" align="center" sx={{ color: alpha('#fff', 0.7), mb: 4 }} paragraph>
              Choose a location to sign in or book an appointment. Your shop selection will be remembered until you return to this page.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <SearchField
                fullWidth
                label="Search Shops by Name or Address"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearch}
                sx={{ maxWidth: 500 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </GlassPaper>
            
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4, 
                background: 'rgba(255, 50, 50, 0.1)', 
                color: '#fff', 
                border: '1px solid rgba(255, 50, 50, 0.2)' 
              }}
            >
              {error}
            </Alert>
          )}
            
          <Grid container spacing={3}>
            {renderShopCards()}
          </Grid>
        
          <Snackbar
            open={Boolean(copiedLink)}
            autoHideDuration={3000}
            onClose={() => setCopiedLink(null)}
            message={`Copied shop link to clipboard`}
          />
        </motion.div>
      </Container>
    </GradientBackground>
  );
};

export default ShopsListPage; 